<template>
  <app-content class="original-voucher-workbench frame-page">
    <div class="page-header">
      <div>
        <h2>原始凭证识别</h2>
        <p>上传店铺订单或资金流水截图，系统将自动识别结构化字段并写入标准订单/流水，便于继续生成财务凭证。</p>
      </div>
      <div class="current-account" v-if="currentAccountSets && currentAccountSets.companyName">
        当前账套：
        <span class="account-name">{{ currentAccountSets.companyName }}</span>
      </div>
    </div>

    <Tabs :datas="tabDatas" v-model="activeTab" class="h-tabs-card" />

    <div v-show="activeTab === 'shop'" class="tab-body">
      <Row :space="20">
        <Cell :width="9">
          <div class="h-panel form-panel">
            <div class="h-panel-bar">
              <span class="h-panel-title">识别参数</span>
            </div>
            <div class="h-panel-body">
              <Form :labelWidth="100">
                <FormItem label="电商平台">
                  <Select
                    v-model="shopForm.platform"
                    :datas="platformOptions"
                    keyName="value"
                    titleName="label"
                    placeholder="选择平台"
                  />
                </FormItem>
                <FormItem label="截图文件">
                  <div class="upload-row">
                    <Button type="primary" @click="triggerShopFile" :loading="shopUploading">
                      {{ shopForm.fileUrl ? '重新上传' : '上传截图' }}
                    </Button>
                    <span class="file-name" v-if="shopForm.fileName">{{ shopForm.fileName }}</span>
                    <span v-if="shopForm.fileUrl" class="link-danger" @click="clearShopFile">移除</span>
                  </div>
                  <input
                    ref="shopFileInput"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    style="display: none"
                    @change="handleShopFileChange"
                  >
                  <div class="upload-tip">支持 JPG/PNG/PDF，建议清晰度≥300dpi。</div>
                </FormItem>
                <FormItem>
                  <Button type="primary" block :loading="shopRecognizing" @click="handleShopRecognize">
                    {{ shopRecognizing ? '识别中...' : '开始识别' }}
                  </Button>
                </FormItem>
              </Form>
            </div>
          </div>
        </Cell>

        <Cell :width="15">
          <div class="h-panel result-panel">
            <div class="h-panel-bar">
              <span class="h-panel-title">识别结果</span>
              <div class="panel-actions">
                <span v-if="shopResult && shopResult.standardOrderId" class="status-tag success">
                  已写入标准订单 #{{ shopResult.standardOrderId }}
                </span>
                <span
                  v-if="shopResult && shopResult.originalVoucherId"
                  class="link-feedback"
                  @click="openFeedbackDialog('shop')"
                >
                  反馈识别问题
                </span>
              </div>
            </div>
            <div class="h-panel-body">
              <div v-if="!shopResult" class="empty-state">
                <i class="h-icon-upload"></i>
                <p>上传截图并点击识别，结果将显示在此处。</p>
              </div>
              <div v-else class="result-content">
                <table class="result-summary">
                  <tbody>
                    <tr>
                      <th>原始凭证ID</th>
                      <td>{{ shopResult.originalVoucherId || '-' }}</td>
                      <th>平台</th>
                      <td>{{ shopResult.platform || shopForm.platform || '-' }}</td>
                    </tr>
                    <tr>
                      <th>订单号</th>
                      <td>{{ shopResult.order_no || shopResult.orderNo || '-' }}</td>
                      <th>订单时间</th>
                      <td>{{ formatDateTime(shopResult.order_time || shopResult.orderTime) || '-' }}</td>
                    </tr>
                    <tr>
                      <th>买家</th>
                      <td>{{ shopResult.buyer_name || shopResult.buyerName || '-' }}</td>
                      <th>订单状态</th>
                      <td>{{ shopResult.order_status || shopResult.orderStatus || '-' }}</td>
                    </tr>
                    <tr>
                      <th>净额</th>
                      <td colspan="3">
                        {{ formatCurrency(shopResult.net_amount || shopResult.netAmount, shopResult.currency) }}
                        <span class="amount-detail">
                          （含税 {{ formatCurrency(shopResult.tax_amount || shopResult.taxAmount, shopResult.currency) }}，
                          运费 {{ formatCurrency(shopResult.shipping_amount || shopResult.shippingAmount, shopResult.currency) }}）
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h4>订单行</h4>
                <table class="result-table">
                  <thead>
                    <tr>
                      <th style="width: 60px">#</th>
                      <th>商品标题</th>
                      <th style="width: 140px">SKU</th>
                      <th style="width: 90px">数量</th>
                      <th style="width: 120px">单价</th>
                      <th style="width: 120px">金额</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in shopItems" :key="index">
                      <td>{{ item.line_no || item.lineNo || index + 1 }}</td>
                      <td>{{ item.title || '-' }}</td>
                      <td>{{ item.sku || '-' }}</td>
                      <td>{{ item.qty || item.quantity || '-' }}</td>
                      <td>{{ formatCurrency(item.unit_price || item.unitPrice, shopResult.currency) }}</td>
                      <td>{{ formatCurrency(item.line_amount || item.lineAmount, shopResult.currency) }}</td>
                    </tr>
                    <tr v-if="!shopItems.length">
                      <td colspan="6" class="empty-cell">暂无行数据</td>
                    </tr>
                  </tbody>
                </table>

                <h4>OCR原始文本</h4>
                <pre class="json-preview">{{ formatJson(shopResult.ocrRecognitionInfo || shopResult.rawOcrData) }}</pre>
              </div>
            </div>
          </div>
        </Cell>
      </Row>
    </div>

    <div v-show="activeTab === 'statement'" class="tab-body">
      <Row :space="20">
        <Cell :width="9">
          <div class="h-panel form-panel">
            <div class="h-panel-bar">
              <span class="h-panel-title">识别参数</span>
            </div>
            <div class="h-panel-body">
              <Form :labelWidth="100">
                <FormItem label="流水来源">
                  <Select
                    v-model="statementForm.platform"
                    :datas="statementPlatformOptions"
                    keyName="value"
                    titleName="label"
                    placeholder="选择来源"
                  />
                </FormItem>
                <FormItem label="截图文件">
                  <div class="upload-row">
                    <Button type="primary" @click="triggerStatementFile" :loading="statementUploading">
                      {{ statementForm.fileUrl ? '重新上传' : '上传截图' }}
                    </Button>
                    <span class="file-name" v-if="statementForm.fileName">{{ statementForm.fileName }}</span>
                    <span v-if="statementForm.fileUrl" class="link-danger" @click="clearStatementFile">移除</span>
                  </div>
                  <input
                    ref="statementFileInput"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    style="display: none"
                    @change="handleStatementFileChange"
                  >
                  <div class="upload-tip">建议上传截图或单页 PDF，包含完整流水表格。</div>
                </FormItem>
                <FormItem>
                  <Button type="primary" block :loading="statementRecognizing" @click="handleStatementRecognize">
                    {{ statementRecognizing ? '识别中...' : '开始识别' }}
                  </Button>
                </FormItem>
              </Form>
            </div>
          </div>
        </Cell>

        <Cell :width="15">
          <div class="h-panel result-panel">
            <div class="h-panel-bar">
              <span class="h-panel-title">识别结果</span>
              <div class="panel-actions">
                <span v-if="statementResult && statementResult.standardTransactionCount" class="status-tag success">
                  已写入 {{ statementResult.standardTransactionCount }} 条流水
                </span>
                <span
                  v-if="statementResult && statementResult.originalVoucherId"
                  class="link-feedback"
                  @click="openFeedbackDialog('statement')"
                >
                  反馈识别问题
                </span>
              </div>
            </div>
            <div class="h-panel-body">
              <div v-if="!statementResult" class="empty-state">
                <i class="h-icon-upload"></i>
                <p>上传资金流水截图（跨境收款/PayPal/银行等）即可查看识别结果。</p>
              </div>
              <div v-else class="result-content">
                <table class="result-summary">
                  <tbody>
                    <tr>
                      <th>原始凭证ID</th>
                      <td>{{ statementResult.originalVoucherId || '-' }}</td>
                      <th>账户ID</th>
                      <td>{{ statementResult.account_id || statementResult.accountId || '-' }}</td>
                    </tr>
                    <tr>
                      <th>币种</th>
                      <td>{{ statementResult.currency || 'USD' }}</td>
                      <th>流水条数</th>
                      <td>{{ statementResult.standardTransactionCount || statementLines.length }}</td>
                    </tr>
                  </tbody>
                </table>

                <h4>流水行</h4>
                <div class="table-scroll">
                  <table class="result-table">
                    <thead>
                      <tr>
                        <th style="width: 150px">交易时间</th>
                        <th style="width: 120px">类型</th>
                        <th>描述</th>
                        <th style="width: 120px">交易金额</th>
                        <th style="width: 120px">手续费</th>
                        <th style="width: 120px">净额</th>
                        <th style="width: 140px">余额</th>
                        <th style="width: 160px">参考ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(line, index) in statementLines" :key="index">
                        <td>{{ formatDateTime(line.txn_time || line.txnTime) || '-' }}</td>
                        <td>{{ line.txn_type || line.txnType || '-' }}</td>
                        <td>{{ line.description || '-' }}</td>
                        <td>{{ formatCurrency(line.gross_amount || line.grossAmount, statementResult.currency) }}</td>
                        <td>{{ formatCurrency(line.fee_amount || line.feeAmount, statementResult.currency) }}</td>
                        <td>{{ formatCurrency(line.net_amount || line.netAmount, statementResult.currency) }}</td>
                        <td>{{ formatCurrency(line.balance_after || line.balanceAfter, statementResult.currency) }}</td>
                        <td>{{ line.reference_id || line.referenceId || '-' }}</td>
                      </tr>
                      <tr v-if="!statementLines.length">
                        <td colspan="8" class="empty-cell">暂无流水数据</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4>OCR原始文本</h4>
                <pre class="json-preview">{{ formatJson(statementResult.ocrRecognitionInfo || statementResult.rawOcrData) }}</pre>
              </div>
            </div>
          </div>
        </Cell>
      </Row>
    </div>
    <Modal
      v-model="feedbackDialogVisible"
      title="反馈识别问题"
      width="520"
      :mask-closable="false"
    >
      <Form :labelWidth="100">
        <FormItem label="问题类型">
          <Select
            v-model="feedbackForm.issueType"
            :datas="feedbackOptions"
            keyName="value"
            titleName="label"
          />
        </FormItem>
        <FormItem label="问题描述" required>
          <textarea
            v-model="feedbackForm.description"
            class="h-input"
            rows="4"
            placeholder="请描述错误字段、金额或其他识别异常"
          ></textarea>
        </FormItem>
        <FormItem label="联系方式">
          <input
            v-model="feedbackForm.contact"
            type="text"
            class="h-input"
            placeholder="可选：手机号/邮箱"
          >
        </FormItem>
        <FormItem>
          <Alert type="info">
            反馈将同步给管理员，便于优化识别模板。若有截图或备注，可在批量任务中补充。
          </Alert>
        </FormItem>
      </Form>
      <div slot="footer">
        <Button @click="feedbackDialogVisible = false">取消</Button>
        <Button type="primary" :loading="feedbackSubmitting" @click="submitFeedback">提交</Button>
      </div>
    </Modal>
  </app-content>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'OriginalVoucherWorkbench',
  data() {
    return {
      tabDatas: [
        { title: '店铺订单截图', key: 'shop' },
        { title: '资金流水截图', key: 'statement' }
      ],
      activeTab: 'shop',
      platformOptions: [
        { label: '亚马逊', value: 'amazon' },
        { label: 'Shopify', value: 'shopify' },
        { label: 'Shopee', value: 'shopee' },
        { label: 'Lazada', value: 'lazada' },
        { label: '其它平台', value: 'custom' }
      ],
      shopForm: {
        platform: 'amazon',
        fileUrl: '',
        fileName: '',
        userId: undefined
      },
      statementForm: {
        platform: 'stripe',
        fileUrl: '',
        fileName: '',
        userId: undefined
      },
      statementPlatformOptions: [
        { label: '跨境收单平台', value: 'stripe' },
        { label: 'PayPal', value: 'paypal' },
        { label: '银行网银/对公账户', value: 'bank' },
        { label: '其它渠道', value: 'custom' }
      ],
      shopResult: null,
      statementResult: null,
      shopUploading: false,
      statementUploading: false,
      shopRecognizing: false,
      statementRecognizing: false,
      feedbackDialogVisible: false,
      feedbackSubmitting: false,
      feedbackTarget: null,
      feedbackForm: {
        issueType: 'FIELD_ERROR',
        description: '',
        contact: ''
      },
      feedbackOptions: [
        { label: '字段错误', value: 'FIELD_ERROR' },
        { label: '金额不符', value: 'AMOUNT_ERROR' },
        { label: '不支持的版式', value: 'UNSUPPORTED' },
        { label: '其他问题', value: 'OTHER' }
      ]
    }
  },
  computed: {
    ...mapState(['currentAccountSets', 'myAccountSets', 'User']),
    currentAccountId() {
      return this.currentAccountSets && this.currentAccountSets.id
    },
    shopItems() {
      if (!this.shopResult) return []
      const items = this.shopResult.items || this.shopResult.orderItems || this.shopResult.order_items
      return Array.isArray(items) ? items : []
    },
    statementLines() {
      if (!this.statementResult) return []
      const lines = this.statementResult.lines || this.statementResult.transactions || this.statementResult.standard_transactions
      return Array.isArray(lines) ? lines : []
    }
  },
  watch: {
    User: {
      handler(val) {
        if (val && val.id) {
          this.shopForm.userId = val.id
          this.statementForm.userId = val.id
        }
      },
      immediate: true
    }
  },
  methods: {
    triggerShopFile() {
      this.$refs.shopFileInput && this.$refs.shopFileInput.click()
    },
    triggerStatementFile() {
      this.$refs.statementFileInput && this.$refs.statementFileInput.click()
    },
    handleShopFileChange(e) {
      const file = e.target.files[0]
      if (!file) {
        return
      }
      this.uploadFile(file, 'shop')
      e.target.value = ''
    },
    handleStatementFileChange(e) {
      const file = e.target.files[0]
      if (!file) {
        return
      }
      this.uploadFile(file, 'statement')
      e.target.value = ''
    },
    uploadFile(file, type) {
      const limit = 10 * 1024 * 1024
      if (file.size > limit) {
        this.$Message('单个文件不能超过10MB')
        return
      }
      const formData = new FormData()
      formData.append('file', file)
      if (type === 'shop') {
        this.shopUploading = true
      } else {
        this.statementUploading = true
      }
      this.$api.upload.file(formData, 'original-voucher').then(({ data }) => {
        if (type === 'shop') {
          this.shopForm.fileUrl = data.filePath
          this.shopForm.fileName = data.fileName || file.name
          this.shopResult = null
        } else {
          this.statementForm.fileUrl = data.filePath
          this.statementForm.fileName = data.fileName || file.name
          this.statementResult = null
        }
        this.$Message('文件上传成功')
      }).finally(() => {
        if (type === 'shop') {
          this.shopUploading = false
        } else {
          this.statementUploading = false
        }
      })
    },
    clearShopFile() {
      this.shopForm.fileUrl = ''
      this.shopForm.fileName = ''
      this.shopResult = null
    },
    clearStatementFile() {
      this.statementForm.fileUrl = ''
      this.statementForm.fileName = ''
      this.statementResult = null
    },
    openFeedbackDialog(type) {
      const targetVoucherId = type === 'shop'
        ? (this.shopResult && this.shopResult.originalVoucherId)
        : (this.statementResult && this.statementResult.originalVoucherId)
      if (!targetVoucherId) {
        this.$Message('暂未生成原始凭证，无法反馈')
        return
      }
      this.feedbackTarget = {
        type,
        voucherId: targetVoucherId
      }
      this.feedbackForm = {
        issueType: 'FIELD_ERROR',
        description: '',
        contact: this.feedbackForm.contact || ''
      }
      this.feedbackDialogVisible = true
    },
    submitFeedback() {
      if (!this.feedbackTarget || !this.feedbackTarget.voucherId) {
        this.$Message('缺少反馈目标')
        return
      }
      if (!this.feedbackForm.description) {
        this.$Message('请填写问题描述')
        return
      }
      this.feedbackSubmitting = true
      const payload = {
        issueType: this.feedbackForm.issueType,
        description: this.feedbackForm.description,
        reporterContact: this.feedbackForm.contact
      }
      this.$api.originalVoucher.submitFeedback(this.feedbackTarget.voucherId, payload).then(() => {
        this.$Message('反馈已提交')
        this.feedbackDialogVisible = false
      }).finally(() => {
        this.feedbackSubmitting = false
      })
    },
    handleShopRecognize() {
      const accountSetsId = this.currentAccountId
      if (!accountSetsId) {
        this.$Message('请先在顶部切换到目标账套')
        return
      }
      if (!this.shopForm.fileUrl) {
        this.$Message('请上传订单截图')
        return
      }
      this.shopRecognizing = true
      const payload = {
        accountSetsId,
        userId: this.shopForm.userId,
        platform: this.shopForm.platform,
        fileUrl: this.shopForm.fileUrl
      }
      this.$api.originalVoucher.recognizeShopOrder(payload).then(({ data }) => {
        this.shopResult = data || null
        this.$Message('识别完成')
      }).finally(() => {
        this.shopRecognizing = false
      })
    },
    handleStatementRecognize() {
      const accountSetsId = this.currentAccountId
      if (!accountSetsId) {
        this.$Message('请先在顶部切换到目标账套')
        return
      }
      if (!this.statementForm.fileUrl) {
        this.$Message('请上传流水截图')
        return
      }
      this.statementRecognizing = true
      const payload = {
        accountSetsId,
        userId: this.statementForm.userId,
        fileUrl: this.statementForm.fileUrl,
        platform: this.statementForm.platform
      }
      this.$api.originalVoucher.recognizeStatement(payload).then(({ data }) => {
        this.statementResult = data || null
        this.$Message('识别完成')
      }).finally(() => {
        this.statementRecognizing = false
      })
    },
    formatCurrency(value, currency) {
      if (value === undefined || value === null || value === '') {
        return '-'
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return value
      }
      const code = currency ? String(currency).toUpperCase() : 'CNY'
      return new Intl.NumberFormat('zh-CN', {
        style: 'currency',
        currency: code
      }).format(num)
    },
    formatDateTime(value) {
      if (!value) {
        return ''
      }
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    formatJson(value) {
      if (!value) {
        return '无'
      }
      if (typeof value === 'string') {
        return value
      }
      try {
        return JSON.stringify(value, null, 2)
      } catch (e) {
        return String(value)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.original-voucher-workbench {
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 22px;
    }

    .current-account {
      color: @gray1-color;

      .account-name {
        font-weight: 600;
        color: @primary-color;
        margin-left: 6px;
      }
    }
  }

  .tab-body {
    margin-top: 20px;
  }

  .form-panel,
  .result-panel {
    min-height: 480px;
  }

  .panel-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .upload-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .file-name {
    color: @gray1-color;
  }

  .link-danger {
    color: #f56c6c;
    cursor: pointer;
    font-size: 12px;
  }

  .link-feedback {
    color: @primary-color;
    cursor: pointer;
    font-size: 12px;
  }

  .upload-tip {
    margin-top: 6px;
    font-size: 12px;
    color: @gray2-color;
  }

  .status-tag {
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 12px;
    &.success {
      background: rgba(82, 196, 26, 0.15);
      color: #39a12d;
    }
  }

  .empty-state {
    text-align: center;
    color: @gray2-color;
    padding: 60px 20px;
    i {
      font-size: 46px;
      color: @gray3-color;
    }
    p {
      margin-top: 12px;
    }
  }

  .result-summary {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;

    th, td {
      border: 1px solid @gray4-color;
      padding: 8px 12px;
      text-align: left;
      font-size: 13px;
    }

    th {
      width: 120px;
      background: #fafafa;
    }

    .amount-detail {
      margin-left: 8px;
      color: @gray2-color;
      font-size: 12px;
    }
  }

  .result-table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      border: 1px solid @gray4-color;
      padding: 8px;
      font-size: 13px;
      text-align: left;
    }

    th {
      background: #f7f8fa;
    }

    .empty-cell {
      text-align: center;
      color: @gray2-color;
    }
  }

  .table-scroll {
    max-height: 300px;
    overflow: auto;
  }

  .json-preview {
    background: #f8f9fb;
    border-radius: 4px;
    padding: 12px;
    font-size: 12px;
    white-space: pre-wrap;
    max-height: 220px;
    overflow: auto;
  }
}
</style>
